<template>
  <section class="sale-payment-plan-box">
    <header class="sale-payment-plan-box__head">
      <div>
        <h4>{{ flatRateText.box.title }}</h4>
        <p>{{ flatRateText.box.subtitle }}</p>
      </div>
    </header>

    <div class="payment-frequency-box">
      <span class="payment-frequency-box__label">{{ flatRateText.frequency.label }}</span>
      <div class="billing-status-row">
        <label
          v-for="option in frequencyCheckboxOptions"
          :key="option.value"
          class="app-modal-toggle billing-toggle"
        >
          <input
            type="checkbox"
            :checked="selectedFrequency === option.value"
            @change="selectFrequency(option.value)"
          />
          <span>{{ option.label }}</span>
        </label>
      </div>

      <div v-if="showRecurringFields" class="payment-recurring-grid payment-recurring-grid--three-columns">
        <label class="app-modal-field payment-recurring-count">
          <span>{{ flatRateText.frequency.paymentsCountLabel }}</span>
          <input
            v-model.number="form.paymentsCount"
            data-focus-field="paymentsCount"
            type="number"
            min="1"
            step="1"
            class="form-control payment-recurring-count__input"
            :class="{ 'is-invalid': hasFieldError('paymentsCount') }"
            :placeholder="flatRateText.frequency.paymentsCountPlaceholder"
            @blur="touchField('paymentsCount')"
          />
          <div class="invalid-feedback d-block" v-if="hasFieldError('paymentsCount')">
            <span v-if="v$.form.paymentsCount.$errors.length">
              {{ v$.form.paymentsCount.$errors[0].$message }}
            </span>
          </div>
        </label>

        <div v-if="showMonthlyFields" class="app-modal-field">
          <span>{{ flatRateText.frequency.firstInstallmentLabel }}</span>
          <multiselect
            ref="firstInstallmentSelect"
            v-model="form.firstInstallmentOption"
            data-focus-field="firstInstallmentOption"
            :options="firstInstallmentOptions"
            track-by="value"
            label="label"
            :show-labels="false"
            :searchable="true"
            :allow-empty="true"
            :max-height="152"
            open-direction="down"
            :class="['app-modal-select', { 'is-invalid': hasFieldError('firstInstallmentOption') }]"
            :placeholder="flatRateText.frequency.firstInstallmentPlaceholder"
            @open="focusSelectedFirstInstallment"
            @close="touchField('firstInstallmentOption')"
          >
            <template #noResult><span>{{ flatRateText.common.noOption }}</span></template>
            <template #noOptions><span>{{ flatRateText.common.noOption }}</span></template>
          </multiselect>
          <div class="invalid-feedback d-block" v-if="hasFieldError('firstInstallmentOption')">
            <span v-if="v$.form.firstInstallmentOption.$errors.length">
              {{ v$.form.firstInstallmentOption.$errors[0].$message }}
            </span>
          </div>
        </div>

        <div v-if="showMultipleFields" class="app-modal-field">
          <span>{{ flatRateText.frequency.periodicityLabel }}</span>
          <multiselect
            v-model="form.periodicityOption"
            data-focus-field="periodicityOption"
            :options="options.periodicities"
            track-by="value"
            label="label"
            :show-labels="false"
            :searchable="true"
            :allow-empty="true"
            :class="['app-modal-select', { 'is-invalid': hasFieldError('periodicityOption') }]"
            :placeholder="flatRateText.frequency.periodicityPlaceholder"
            @close="touchField('periodicityOption')"
          >
            <template #noResult><span>{{ flatRateText.common.noOption }}</span></template>
            <template #noOptions><span>{{ flatRateText.common.noOption }}</span></template>
          </multiselect>
          <div class="invalid-feedback d-block" v-if="hasFieldError('periodicityOption')">
            <span v-if="v$.form.periodicityOption.$errors.length">
              {{ v$.form.periodicityOption.$errors[0].$message }}
            </span>
          </div>
        </div>

        <label class="app-modal-field">
          <span>{{ flatRateText.amounts.subtotalLabel }}</span>
          <AppMoneyInput
            v-model="form.subtotal"
            output-mode="number"
            :decimal-places="2"
            :minimum-value="0"
            :currency="currencyModel"
          />
          <small class="expense-field-warning">
            {{ businessAmountLegend }}
          </small>
        </label>
      </div>
    </div>

    <label v-if="!showRecurringFields" class="app-modal-field">
      <span>{{ flatRateText.amounts.subtotalLabel }}</span>
      <AppMoneyInput
        v-model="form.subtotal"
        output-mode="number"
        :decimal-places="2"
        :minimum-value="0"
        :currency="currencyModel"
      />
      <small class="expense-field-warning">
        {{ businessAmountLegend }}
      </small>
    </label>

    <AppGeneralTaxesBlock
      :show-taxes-section="showTaxesSection"
      :show-tax-selectors="showTaxSelectors"
      :without-taxes-enabled="withoutTaxesEnabled"
      :form="form"
      :options="options"
      :text="taxesText"
      :common-text="taxesCommonText"
      @toggle-without-taxes="toggleWithoutTaxes"
    />

    <AppGeneralTotalsSummary
      :tax-preview="taxPreview"
      :show-tax-breakdown-summary="showTaxesSection && !withoutTaxesEnabled"
      :show-tax-section-breakdown="showTaxSectionBreakdown"
      :transferred-tax-items="transferredTaxItems"
      :withheld-tax-items="withheldTaxItems"
      :format-money="formatMoney"
      :format-tax-breakdown-line="formatTaxBreakdownLine"
    />
  </section>
</template>

<script>
import AppMoneyInput from '@/components/common/AppMoneyInput.vue';
import AppGeneralTaxesBlock from '@/components/common/AppGeneralTaxesBlock.vue';
import AppGeneralTotalsSummary from '@/components/common/AppGeneralTotalsSummary.vue';
import { buildTodayDate } from '@/utils/datePresentation';
import { resolveExpenseFeeVoucherFlags } from '@/composables/expensesFees/shared/useExpenseFeeTaxFlow';
import {
  formatPaymentPlanMoney,
  formatPaymentPlanTaxBreakdownLine,
} from '@/composables/paymentPlans/usePaymentPlanTaxFlow';
import {
  applyFrequencyRules,
  applyVoucherTypeRules,
  applyWithoutTaxesRules,
} from '@/composables/paymentPlans/usePaymentPlanGeneralTransitions';
import { FLAT_RATES_TEXT } from '@/texts/catalog/flatRates';

const FLAT_RATE_TEXT = FLAT_RATES_TEXT.modal.flatRatePlan;

export default {
  name: 'SaleModalFlatRatePaymentPlanBox',
  components: {
    AppMoneyInput,
    AppGeneralTaxesBlock,
    AppGeneralTotalsSummary,
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      required: true,
    },
    firstInstallmentOptions: {
      type: Array,
      default: () => [],
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    taxPreview: {
      type: Object,
      required: true,
    },
    v$: {
      type: Object,
      default: null,
    },
  },
  computed: {
    flatRateText() {
      return FLAT_RATE_TEXT;
    },
    currencyModel() {
      const current = String(this.form?.currencyCode || '').trim().toUpperCase();
      return current !== '' ? current : 'MXN';
    },
    showRecurringFields() {
      return String(this.form?.frequency || '').trim().toLowerCase() !== 'unique';
    },
    showMonthlyFields() {
      return String(this.form?.frequency || '').trim().toLowerCase() === 'monthly';
    },
    showMultipleFields() {
      return String(this.form?.frequency || '').trim().toLowerCase() === 'multiple';
    },
    selectedFrequency() {
      const frequency = String(this.form?.frequency || '').trim().toLowerCase();
      if (['unique', 'monthly', 'multiple'].includes(frequency)) {
        return frequency;
      }

      return 'monthly';
    },
    frequencyCheckboxOptions() {
      const options = [
        { value: 'unique', label: this.flatRateText.frequency.options.unique },
        { value: 'monthly', label: this.flatRateText.frequency.options.monthly },
        { value: 'multiple', label: this.flatRateText.frequency.options.multiple },
      ];

      if (!this.isEditMode) {
        return options.filter((option) => String(option?.value || '').trim().toLowerCase() !== 'unique');
      }

      return options;
    },
    voucherType() {
      return String(this.form?.voucherType || '').trim().toLowerCase();
    },
    isSimpleNoteVoucher() {
      return resolveExpenseFeeVoucherFlags(this.voucherType).isSimpleNoteVoucher;
    },
    showTaxesSection() {
      return !this.isSimpleNoteVoucher;
    },
    showTaxSelectors() {
      return this.showTaxesSection && !this.withoutTaxesEnabled;
    },
    withoutTaxesEnabled() {
      return Boolean(this.form?.withoutTaxes);
    },
    taxesText() {
      return this.flatRateText.taxes;
    },
    taxesCommonText() {
      return {
        noOption: this.flatRateText.common.noOption,
      };
    },
    taxBreakdownItems() {
      return Array.isArray(this.taxPreview?.taxes?.items)
        ? this.taxPreview.taxes.items
        : [];
    },
    transferredTaxItems() {
      return this.taxBreakdownItems
        .filter((item) => String(item?.direction || '').trim().toLowerCase() === 'trasladado');
    },
    withheldTaxItems() {
      return this.taxBreakdownItems
        .filter((item) => String(item?.direction || '').trim().toLowerCase() === 'retenido');
    },
    showTaxSectionBreakdown() {
      if (!this.showTaxesSection) {
        return false;
      }

      return String(this.taxPreview?.tax_type || this.form?.taxType || '').trim().toLowerCase() === 'group';
    },
    currentAmountNumber() {
      return Number(this.form?.subtotal || 0) || 0;
    },
    installmentsCount() {
      if (!this.showRecurringFields) {
        return 1;
      }

      const count = Number(this.form?.paymentsCount || 1) || 1;
      return Math.max(1, count);
    },
    plannedAmountTotal() {
      return this.currentAmountNumber * this.installmentsCount;
    },
    businessAmountLegend() {
      return `Importe del negocio: ${this.formatMoney(this.plannedAmountTotal)}`;
    },
  },
  watch: {
    'form.frequency'(nextValue) {
      if (!this.isEditMode && String(nextValue || '').trim().toLowerCase() === 'unique') {
        this.form.frequency = 'monthly';
        return;
      }

      applyFrequencyRules(this.form, nextValue, this.firstInstallmentOptions);
    },
    isEditMode: {
      immediate: true,
      handler() {
        this.ensureCreateFrequency();
      },
    },
    firstInstallmentOptions(nextOptions) {
      if (String(this.form?.frequency || '').trim().toLowerCase() !== 'monthly') {
        return;
      }

      applyFrequencyRules(this.form, 'monthly', nextOptions);
    },
    'form.withoutTaxes'(nextValue) {
      applyWithoutTaxesRules(this.form, nextValue);
    },
    'form.voucherType': {
      immediate: true,
      handler(nextValue) {
        applyVoucherTypeRules(this.form, nextValue);

        if (!this.isSimpleNoteVoucher && !this.form.cfdiDate) {
          this.form.cfdiDate = buildTodayDate();
        }

        this.ensureDefaultTaxSelection();
      },
    },
    'options.taxes': {
      deep: true,
      handler() {
        this.ensureDefaultTaxSelection();
      },
    },
  },
  methods: {
    ensureCreateFrequency() {
      if (this.isEditMode) {
        return;
      }

      if (String(this.form?.frequency || '').trim().toLowerCase() === 'unique') {
        this.form.frequency = 'monthly';
      }
    },
    resolveDefaultTaxOption() {
      const taxes = Array.isArray(this.options?.taxes) ? this.options.taxes : [];
      if (taxes.length === 0) {
        return null;
      }

      const explicitDefault = taxes.find((option) => Number(option?.meta?.default || 0) === 1);
      if (explicitDefault) {
        return explicitDefault;
      }

      const iva16 = taxes.find((option) => {
        const label = String(option?.label || option?.meta?.name || '').trim().toLowerCase();
        const rate = Number(option?.meta?.rate ?? option?.meta?.percentage ?? 0);
        return (label.includes('iva') && label.includes('16')) || rate === 16;
      });

      return iva16 || taxes[0] || null;
    },
    ensureDefaultTaxSelection() {
      if (!this.showTaxesSection || this.withoutTaxesEnabled) {
        return;
      }

      const hasSelectedGroup = Number(this.form?.taxGroup?.value || 0) > 0;
      const hasSelectedTax = (Array.isArray(this.form?.taxes) ? this.form.taxes : []).length > 0;
      if (hasSelectedGroup || hasSelectedTax) {
        return;
      }

      const defaultTax = this.resolveDefaultTaxOption();
      if (!defaultTax) {
        return;
      }

      this.form.taxType = 'individual';
      this.form.taxGroup = null;
      this.form.taxes = [defaultTax];
    },
    selectFrequency(frequencyValue) {
      this.form.frequency = String(frequencyValue || 'monthly').trim().toLowerCase();
    },
    focusSelectedFirstInstallment() {
      const selectedValue = String(this.form?.firstInstallmentOption?.value || '').trim();
      if (selectedValue === '') {
        return;
      }

      const runOnFrame = typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (callback) => setTimeout(callback, 0);

      this.$nextTick(() => {
        runOnFrame(() => {
          const selectRef = this.$refs.firstInstallmentSelect;
          const selectVm = Array.isArray(selectRef) ? selectRef[0] : selectRef;
          const rootElement = selectVm?.$el || selectVm;

          if (!rootElement || typeof rootElement.querySelector !== 'function') {
            return;
          }

          const contentWrapper = rootElement.querySelector('.multiselect__content-wrapper');
          if (!contentWrapper) {
            return;
          }

          const selectedOption = contentWrapper.querySelector('.multiselect__option--selected');
          if (selectedOption && typeof selectedOption.scrollIntoView === 'function') {
            selectedOption.scrollIntoView({ block: 'nearest' });
          }
        });
      });
    },
    toggleWithoutTaxes(event) {
      if (!this.showTaxesSection) {
        return;
      }

      const checked = Boolean(event?.target?.checked);
      this.form.withoutTaxes = checked;
      applyWithoutTaxesRules(this.form, checked);
    },
    hasFieldError(fieldName) {
      return Boolean(this.v$?.form?.[fieldName]?.$error);
    },
    touchField(fieldName) {
      if (!this.v$?.form || !this.v$.form[fieldName]) {
        return;
      }

      this.v$.form[fieldName].$touch();
    },
    formatMoney(value) {
      return formatPaymentPlanMoney(value, this.form?.currencyCode);
    },
    formatTaxBreakdownLine(item) {
      return formatPaymentPlanTaxBreakdownLine(item, this.form?.currencyCode);
    },
  },
};
</script>

<style scoped>
.sale-payment-plan-box {
  border: 0;
  border-radius: 0;
  padding: 0;
  display: grid;
  gap: 12px;
}

.sale-payment-plan-box__head h4 {
  margin: 0;
  font-size: 14px;
  color: #1f2937;
}

.sale-payment-plan-box__head p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #6b7280;
}

.payment-frequency-box {
  display: grid;
  gap: 8px;
}

.payment-frequency-box__label {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
}

.billing-status-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.billing-toggle {
  min-height: 42px;
  border: 1px solid rgba(153, 152, 153, 0.3);
  border-radius: 8px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  margin: 0;
}

.payment-recurring-grid {
  margin-top: 10px;
}

.payment-recurring-grid--three-columns {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.payment-recurring-count__input {
  height: 38px !important;
  min-height: 38px !important;
  max-height: 38px !important;
  line-height: 1.2;
  padding-top: 6px;
  padding-bottom: 6px;
}

.expense-field-warning {
  color: #8a95a6;
}

.expense-field-warning--overrun {
  color: #b45309;
}

@media (max-width: 900px) {
  .billing-status-row {
    grid-template-columns: 1fr;
  }

  .payment-recurring-grid--three-columns {
    grid-template-columns: 1fr;
  }
}
</style>
